import { TableSection } from '@/types/table';
import { ConfirmOptions, ConfirmResult } from 'material-ui-confirm';
import { createContext } from 'react';

export type ErrorObject = {
  title?: string;
  message: string;
  stacktrace?: unknown;
};

type ProgressContext = {
  /**
   * Show a simple dialog with an indeterminate circular progressbar and optional message.
   * @param message message to be shown during progress
   */
  showProgress: (message: string) => void;

  /**
   * Manually close the progress dialog.
   */
  closeProgress: () => void;
};

type TableContext = {
  /**
   * Show one table per section, in the order given. A single table is a single section.
   * @param title string displaying the expected title of the dialog
   * @param sections a table each, of an optional heading, its column headers and its rows
   */
  showTable: (title: string, sections: TableSection[]) => void;
  /**
   * Manually close the progress dialog.
   */
  closeTable: () => void;
};

type ErrorContext = {
  /**
   * Show error dialog message with optional title and stacktrace.
   * @param errorObject pass along all parameters in one object
   * @example
      try {
        //... insert code here
      } catch (error) {
        if (error instanceof Error) {
          showError({
            title: 'An error occured',
            message:
              'This error occured when doing X. See stacktrace for more information.',
            stacktrace: error,
          });
        }
      }
   */
  showError: (errorObject: ErrorObject) => void;
};

type CommonDialogContextType = ProgressContext &
  ErrorContext &
  TableContext & {
    /**
     * This function opens a confirmation dialog and returns a promise that always
     * resolves with the user choice; check `result.confirmed` before acting.
     */
    confirm: (options?: ConfirmOptions | undefined) => Promise<ConfirmResult>;
  };

export const CommonDialogContext = createContext<CommonDialogContextType>({
  showError: () => {},
  showProgress: () => {},
  closeProgress: () => {},
  showTable: () => {},
  closeTable: () => {},
  confirm: () => new Promise<ConfirmResult>(() => {})
});
